import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, { applyNodeChanges, Edge, Position, useEdgesState, useNodesState, useReactFlow }
    from "react-flow-renderer";
import { Network } from "../../fstn/network";
import { connect } from "react-redux";
import { Cursor } from "../../fstn/cursor";
import { selectFocus, selectModeData, selectProject, selectUserId } from "../../redux/selectors/editor";
import { IGraphData, updateFocus, UpdateModeData, updateModeData } from "../../redux/slices/editor";
import { ActionFactory } from "../../fstn/actionFactory";
import { IDebugController } from "../../fstn/debugController";
import { Node, XYPosition } from "react-flow-renderer/dist/types";
import makeStyles from '@mui/styles/makeStyles';
import { FirebaseContext } from "../firebase/FirebaseProvider";
import SelectionBox from "./SelectionBox";
import StateNode from "./StateNode";
import ButtonEdge from "./ButtonEdge";
import RightButtonPan from "./RightButtonPan";
import dagre from "dagre";
import MiniMapDrag from "./MiniMapDrag";
import CursorPan from "./CursorPan";
import {selectConstants, selectLocks, selectProjects} from "../../redux/selectors/firebase";
import { Locks } from "../navbar/StateLock";
import { Projects } from "../../redux/types/firebase";
import NodeStyles from "./node/NodeStyles";
import ChipStyles from "./ChipStyles";
import GraphExporter from "./GraphExporter";
import GroupHighlights from "./groups/GroupHighlights";
import {Constants} from "./StateTags";

const useStyles = makeStyles(theme => ({
    container: {
        width: "100%",
        height: "100%",
        backgroundColor: "lightgray"
    },
    miniMap: {
        borderColor: "#AAA",
        borderRadius: 8,
        borderStyle: "solid",
        borderWidth: 1,
        marginBottom: theme.spacing(6)
    }
}));

const actionHeight = 15;
const maxActionHeight = 60;
const lineHeight = 42;
const nodeWidth = 240;
const nodeHeight = 110;
const transitionHeight = 30;
type Positions = { [state: string]: { x: number, y: number } }

interface IProps
{
    constants: Constants
    cursor: Cursor
    debugController: IDebugController | undefined
    factory: ActionFactory
    graphData: IGraphData
    focus: string
    network: Network
    project: string
    projects: Projects
    locks: Locks
    userId: string
    updateFocus: (focus: string) => void
    updateModeData: UpdateModeData
}
function ReactFlowGraph({constants, cursor, debugController, factory, focus, graphData, network, projects, project,
                            locks, userId, updateFocus, updateModeData }: IProps)
{
    const classes = useStyles();
    const projectDetails = projects[project];
    const localPositionsKey = `localPositions.${project}`
    const [localPositions, setLocalPositions] = useState<Positions>(
        JSON.parse(localStorage.getItem(localPositionsKey) || "{}"));
    const [quickViewCached, setQuickViewCached] = useState(false);
    const [nodes, setNodes] = useNodesState([]);
    const [edges, setEdges] = useEdgesState([]);
    const { setCenter } = useReactFlow();
    const graphRef = useRef<HTMLDivElement>(null);
    const [bounds, setBounds] = useState<{ [state: string]: XYPosition }>({});
    const getHeight = (actions: number, transitions: number, script: string) =>
    {
        return transitions * transitionHeight + Math.max(
            nodeHeight + Math.min(actions * actionHeight, maxActionHeight),
            Math.min(script.length / 32, 8) * lineHeight) + 32;
    }
    const { setStateMember, setStatePosition } = useContext(FirebaseContext);
    const nodeTypes = useMemo(() => ({ stateNode: StateNode }), []);
    const edgeTypes = useMemo(() => ({ buttonEdge: ButtonEdge }), []);
    const userHasLock = useMemo(() =>
    {
        return projectDetails.cookbook ? graphData.useCookbookPositions && locks && graphData.root in locks && locks[graphData.root]?.user === userId : true
    }, [graphData.root, locks, userId, graphData.useCookbookPositions, projectDetails.cookbook]);

    const handleNodesChange = useCallback((changes) =>
    {
        setNodes(nodes =>
        {
            return applyNodeChanges(changes, nodes)
        });
    }, [setNodes]);
    const handleSetSelected = useCallback((state: string) =>
    {
        updateModeData({
            selected: [state],
            selectedActive: state
        });
    }, [updateModeData]);
    const updateElementPositions = useCallback((nodes: Node[], edges: Edge[]) =>
    {
        const graph = new dagre.graphlib.Graph();

        graph.setDefaultEdgeLabel(() => ({}));
        graph.setGraph({
            nodesep: 50,
            ranker: "longest-path",
            rankdir: "LR",
            ranksep: 50
        });
        nodes.forEach(node =>
        {
            graph.setNode(node.id, { width: node.data.width, height: node.data.height });
        });
        edges.forEach(edge =>
        {
            graph.setEdge(edge.source, edge.target);
        });
        // Get the graph layout
        dagre.layout(graph);

        nodes.forEach(node =>
        {
            const graphNode = graph.node(node.id);
            const position = localPositions[node.id];
            const state = network.states.get(node.id);

            if (graphData.useCookbookPositions && state && state.meta.position)
            {
                node.position = { x: state.meta.position.x, y: state.meta.position.y };
            }
            else if (position)
            {
                node.position = { x: position.x, y: position.y };
            }
            else
            {
                node.position = { x: graphNode.x, y: graphNode.y };
            }
        });
    }, [localPositions, network.states, graphData.useCookbookPositions]);
    const handleRearrangeStates = useCallback(() =>
    {
        localStorage.setItem(localPositionsKey, "{}");
        setLocalPositions({});
        updateElementPositions(nodes, edges);
        setNodes([...nodes]);
        setEdges([...edges]);
    }, [edges, localPositionsKey, nodes, setEdges, setNodes, updateElementPositions]);
    const handleNodeDragStop = useCallback((event: React.MouseEvent<Element, MouseEvent>, dragStopNode: Node) =>
    {
        if (graphData.useCookbookPositions)
        {
            setStatePosition(project, dragStopNode.id, { x: dragStopNode.position.x, y: dragStopNode.position.y });
        }
        else
        {
            const updatedPositions = { ...localPositions, [dragStopNode.id]: { ...dragStopNode.position } };
            localStorage.setItem(localPositionsKey, JSON.stringify(updatedPositions));
            setLocalPositions(updatedPositions);
        }
    }, [localPositions, localPositionsKey, graphData.useCookbookPositions, project, setStatePosition]);

    // Set the root node on loading the network 
    useEffect(() =>
    {
        if (network && !graphData.root)
        {
            const initial = network.states.get(network.initial);

            if (initial)
            {
                updateModeData({
                    root: initial.parent,
                    selected: [],
                    selectedActive: ""
                });
            }
        }
    }, [graphData.root, network, updateModeData]);

    // Perform the initial build of the nodes and edges
    useEffect(() =>
    {
        const handleDoubleClickState = (state: string) =>
        {
            updateFocus(state);
            cursor.forceState(state);
        }

        const handleEdgeDelete = (source: string, target: string | undefined, trigger: string | undefined) =>
        {
            const sourceState = network.states.get(source);

            if (sourceState)
            {
                const index = sourceState.transitions.findIndex(transition => transition.destination === target &&
                    transition.trigger === trigger);

                if (index >= 0)
                {
                    const transitions = [...sourceState.transitions];

                    transitions.splice(index, 1);
                    setStateMember(project, source, "transitions", transitions).catch(console.error);
                }
            }
        }

        const handleSetBounds = (id: string, bound: XYPosition) =>
        {
            setBounds(bounds =>
            {
                if (!(id in bounds) || bounds[id].x !== bound.x || bounds[id].y !== bound.y)
                {
                    return { ...bounds, [id]: bound };
                }
                return bounds;
            })
        }

        const stateNodes: Node[] = [];
        const transitionEdges: Edge[] = [];
        const children: { [key: string]: string[] } = {};
        const destinations: { [key: string]: string[] } = {};

        network.states.forEach((state, stateKey) =>
        {
            if (state.parent)
            {
                if (!children[state.parent]) children[state.parent] = [];
                children[state.parent].push(stateKey);
            }
            state.transitions.map(transitions => transitions.destination)
                .filter(destination => destination !== undefined)
                .forEach(destination =>
                {
                    if (destination !== undefined)
                    {
                        if (destinations[destination] === undefined)
                        {
                            destinations[destination] = [];
                        }
                        destinations[destination].push(stateKey);
                    }
                })
        });
        network.states.forEach((state, stateKey) =>
        {
            if (graphData.root !== state.parent) return;

            const width = stateKey in bounds ? bounds[stateKey].x : nodeWidth;
            const height = stateKey in bounds ? bounds[stateKey].y : getHeight(state.actions.length, state.transitions.length, state.script);

            const element: Node = {
                id: stateKey,
                data: {
                    actions: state.actions,
                    current: cursor.state === stateKey,
                    child: state.child,
                    debugController,
                    factory,
                    goto: new Array<{ label: string, destination: string }>(),
                    incoming: destinations[stateKey] || [],
                    initial: stateKey === network.initial,
                    label: state.name,
                    network: network,
                    parent: state.parent,
                    onDoubleClick: handleDoubleClickState,
                    onSetSelected: handleSetSelected,
                    onTransitionDelete: handleEdgeDelete,
                    onSetBounds: handleSetBounds,
                    script: state.script,
                    transitions: state.transitions,
                    meta: state.meta,
                    width,
                    height
                },
                position: { x: 0, y: 0 },
                sourcePosition: Position.Right,
                targetPosition: Position.Left,
                type: "stateNode"
            };

            stateNodes.push(element);
            if (state.child) 
            {
                element.data.goto.push({
                    label: "Child",
                    destination: state.child
                });
            }
            state.transitions.forEach((transition, index) =>
            {
                if (!transition.destination) return;

                const destination = network.states.get(transition.destination);

                if (destination)
                {
                    if (destination.parent !== state.parent)
                    {
                        const destination = network.states.get(transition.destination);
                        const label = (transition.destination === state.parent ? "Up" : transition.trigger) ||
                            destination?.name || "Jump"

                        element.data.goto.push({
                            label,
                            destination: transition.destination
                        });
                    } else if (transition.meta?.type === "jump")
                    {
                        const destination = network.states.get(transition.destination);

                        element.data.goto.push({
                            label: destination ? `Jump to: ${destination.name}` : "Jump",
                            destination: transition.destination
                        });
                    } else
                    {
                        const sourceHeight = getHeight(state.actions.length, state.transitions.length, state.script);
                        const nonTransitionHeight = sourceHeight - state.transitions.length * transitionHeight;
                        const transitionPosition = nonTransitionHeight + index * transitionHeight;

                        transitionEdges.push({
                            id: `${stateKey}.${transition.destination}.${transition.trigger}`,
                            data: {
                                meta: transition.meta,
                                source: {
                                    height: sourceHeight,
                                    width: nodeWidth,
                                    position: transitionPosition
                                },
                                transitionIndex: index,
                                transitionCount: state.transitions.length
                            },
                            source: stateKey,
                            sourceHandle: `${stateKey}.${index}`,
                            target: transition.destination,
                            type: "buttonEdge",
                        });
                    }
                }
            });
        });
        updateElementPositions(stateNodes, transitionEdges);
        setNodes(stateNodes);
        setEdges(transitionEdges);
    }, [constants, cursor, debugController, graphData, setStateMember, factory, handleSetSelected, network,
        project, setNodes, setEdges, bounds, updateElementPositions, updateFocus]);

    // Update the current node on cursor state change
    useEffect(() =>
    {
        setNodes((nodes) => nodes.map(node =>
        {
            node.data.current = (node.id === cursor.state);
            return node;
        }));
    }, [cursor, cursor.state, setNodes]);

    // Handle the escape key popping focus to parent state
    useEffect(() =>
    {
        const handleKeydown = ({ keyCode }: KeyboardEvent) =>
        {
            if (keyCode === 27) // Escape
            {
                const root = network.states.get(graphData.root);

                if (root?.parent)
                {
                    updateFocus(graphData.root);
                    updateModeData({
                        root: root.parent,
                        selected: [],
                        selectedActive: ""
                    });
                    updateModeData({ quickViewState: undefined });
                    updateModeData({ quickViewEdit: undefined });
                }
            }
        };

        window.addEventListener('keydown', handleKeydown);
        return () => window.removeEventListener('keydown', handleKeydown);
    }, [graphData.root, network, updateFocus, updateModeData]);
    // Update the focussed node when the focus changes
    useEffect(() =>
    {
        if (focus)
        {
            const node = nodes.find(node => node.id === focus);

            if (node)
            {
                setCenter(node.position.x, node.position.y, { zoom: 1 });
                updateFocus("");
            } else
            {
                const root = network.states.get(focus)?.parent;

                if (root)
                {
                    updateModeData({
                        root,
                        selected: [],
                        selectedActive: ""
                    });
                }
            }
        }
    }, [focus, network, nodes, setCenter, updateFocus, updateModeData]);
    useEffect(() =>
    {
        if (graphData.events.includes("rearrangeStates"))
        {
            handleRearrangeStates();
            updateModeData({ events: graphData.events.filter(event => event !== "rearrangeStates") });
        }
    }, [graphData, handleRearrangeStates, updateModeData]);
    useEffect(() =>
    {
        const handleKeyDown = ({ key }: KeyboardEvent) =>
        {
            if (key === "q" && !graphData.quickViewEnabled)
            {
                setQuickViewCached(true);
                updateModeData({ quickViewEnabled: true });
            }
        }
        const handleKeyUp = ({ key }: KeyboardEvent) =>
        {
            if (key === "q" && quickViewCached)
            {
                setQuickViewCached(false);
                updateModeData({ quickViewEnabled: false });
            }
        }

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);
        return () =>
        {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
        }
    }, [graphData.quickViewEnabled, quickViewCached, updateModeData]);

    return (
        <div className={classes.container}>
            <NodeStyles constants={constants}/>
            <ChipStyles />
            <GraphExporter graph={graphRef.current} />
            <SelectionBox>
                <RightButtonPan>
                    <CursorPan>
                        <ReactFlow edges={edges} edgeTypes={edgeTypes} nodes={nodes} minZoom={0.01}
                                   nodesDraggable={!graphData.lockedStates && userHasLock} nodeTypes={nodeTypes}
                                   onNodesChange={handleNodesChange} onNodeDragStop={handleNodeDragStop}
                                   panOnDrag={graphData.touchMode} elementsSelectable={false} ref={graphRef}/>
                    </CursorPan>
                </RightButtonPan>
            </SelectionBox>
            <GroupHighlights graph={graphRef.current} />
            <MiniMapDrag />
        </div >
    );
}

const mapStateToProps = (store: never) => ({
    constants: selectConstants(store) ?? {},
    focus: selectFocus(store),
    graphData: selectModeData(store) as IGraphData,
    project: selectProject(store),
    projects: selectProjects(store),
    locks: selectLocks(store),
    userId: selectUserId(store),
});
export default connect(mapStateToProps, {
    updateFocus,
    updateModeData,
})(ReactFlowGraph);
